<template>
  <b-card class="edit-profile-card">
    <div v-if="isLoading" class="loading-wrap">
      <b-spinner label="Loading..." />
    </div>

    <div v-else>
      <div class="edit-head d-flex align-items-start">
        <b-avatar :src="image || ''" size="72" class="mr-1" />
        <div>
          <div class="d-flex align-items-center flex-wrap mb-50">
            <b-button variant="primary" class="mr-1 btn-dark-blue" @click="openProfileUpload">
              Upload new photo
            </b-button>
            <b-button variant="light" class="reset-btn" @click="resetImage">Reset</b-button>
          </div>
          <small class="text-muted">Allowed JPG, GIF or PNG. Max size of 800K</small>
          <b-form-file
            ref="profileUpload"
            v-model="uploadFile"
            accept=".jpg,.jpeg,.png,.gif"
            plain
            hidden
            @input="uploadDoc($event)"
          />
        </div>
      </div>

      <div class="form-wrap">
        <b-row>
          <b-col md="6">
            <b-form-group label="First Name" class="label">
              <b-form-input v-model="firstname" placeholder="John" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Last Name" class="label">
              <b-form-input v-model="lastname" placeholder="Doe" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Phone Number" class="label">
              <b-form-input v-model="phone" placeholder="674 758 7365" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Email Address" class="label">
              <b-form-input v-model="email" placeholder="Address" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Address" class="label">
              <b-form-input v-model="address" placeholder="California" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Zip Code" class="label">
              <b-form-input v-model="pincode" placeholder="34753" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Country" class="label">
              <b-form-select v-model="countryName" :options="countryOptions" />
            </b-form-group>
          </b-col>
          <b-col cols="12" class="mt-50">
            <b-button class="mr-1 btn-dark-blue" variant="primary" @click="updateProfile">Save Changes</b-button>
            <b-button variant="light" class="reset-btn" @click="$router.push('/admin/profile')">Cancel</b-button>
          </b-col>
        </b-row>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BAvatar, BButton, BCard, BCol, BFormFile, BFormGroup, BFormInput, BFormSelect, BRow, BSpinner } from 'bootstrap-vue'
import axios from '@/component/axios'
import adminPanelApi from '@/services/adminPanelApi'
import { getAccessToken } from '@/auth/utils'

export default {
  components: {
    BAvatar,
    BButton,
    BCard,
    BCol,
    BFormFile,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BSpinner,
  },
  data() {
    let user = null
    try {
      user = JSON.parse(localStorage.getItem('userData') || 'null')
    } catch {
      user = null
    }
    return {
      accessToken: localStorage.getItem('accessToken'),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      user,
      isLoading: false,
      uploadFile: null,
      image: '',
      firstname: '',
      lastname: '',
      phone: '',
      email: '',
      address: '',
      pincode: '',
      countryName: 'USA',
      countryOptions: ['USA'],
    }
  },
  mounted() {
    this.accessToken = localStorage.getItem('accessToken')
    try {
      this.user = JSON.parse(localStorage.getItem('userData') || 'null')
    } catch {
      this.user = null
    }
    this.getProfile()
  },
  methods: {
    openProfileUpload() {
      const input = this.$refs.profileUpload
      if (input && input.$el) input.$el.click()
    },
    async getProfile() {
      if (!this.user || !this.user.id) return
      this.isLoading = true
      try {
        const res = await axios({
          method: 'GET',
          url: `${this.baseApi}auth/admin/get-profile/${this.user.id}`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.accessToken}`,
          },
        })
        const data = res?.data?.data || {}
        this.image = data.image || ''
        this.firstname = data.f_name || ''
        this.lastname = data.l_name || ''
        this.phone = data.phone || ''
        this.email = data.email || ''
        this.address = data.address || ''
        this.pincode = data.pincode || ''
        this.countryName = data.country?.name || data.country_name || 'USA'
      } catch (error) {
        console.log(error, 'error')
      } finally {
        this.isLoading = false
      }
    },
    async uploadDoc(file) {
      if (!file) return
      const token = getAccessToken()
      if (!token) {
        this.$swal({
          title: 'Error',
          text: 'Session expired. Please log in again.',
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
        return
      }
      const formData = new FormData()
      formData.append('file', file)
      try {
        const response = await adminPanelApi.uploadImage(formData)
        const data = response?.data?.data
        const imageUrl = typeof data === 'string' ? data : data?.url || response?.data?.url
        if (imageUrl) this.image = imageUrl
      } catch (error) {
        const message = error?.response?.data?.errors?.file?.[0]
          || error?.response?.data?.errors?.message
          || error?.response?.data?.message
          || 'Unable to upload image.'
        this.$swal({
          title: 'Error',
          text: message,
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
      }
    },
    resetImage() {
      this.image = ''
      this.uploadFile = null
    },
    async updateProfile() {
      const payload = {
        image: this.image,
        f_name: this.firstname,
        l_name: this.lastname,
        phone: this.phone,
        email: this.email,
        address: this.address,
        pincode: this.pincode,
      }
      try {
        await axios({
          method: 'PUT',
          url: `${this.baseApi}auth/admin/update/${this.user.id}`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.accessToken}`,
          },
          data: JSON.stringify(payload),
        })
        this.$swal({
          title: 'Submitted',
          text: 'Profile Updated Successfully',
          icon: 'success',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
        this.$router.push('/admin/profile')
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: error?.response?.data?.message || 'Something went wrong',
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
      }
    },
  },
}
</script>

<style scoped lang="scss">
.edit-profile-card {
  border: 1px solid #ebe9f1;
}
.loading-wrap {
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.edit-head {
  padding: 1.2rem;
  border-bottom: 1px solid #ebe9f1;
}
.form-wrap {
  padding: 1rem 1.2rem 1.2rem;
}
.label {
  color: #6e6b7b;
  font-size: 0.82rem;
}
.btn-dark-blue {
  background: #0b1f39 !important;
  border-color: #0b1f39 !important;
}
.reset-btn {
  color: #a8aaae;
  background: #f2f2f3 !important;
  border-color: #f2f2f3 !important;
}
</style>
